import { useState } from "react";
import {
  formatCompactCurrency,
  type DashboardSnapshot,
} from "../../../lib/dashboard";
import type { UseTargetEditOptions } from "../../../lib/profile/use-target-edit";
import type { ReadinessTarget } from "../../../lib/readiness-types";
import { displayReadinessTargetArea, isPropertyReadinessTarget } from "../../../lib/readiness-target-display";

import { LoanProgramRow } from "./rows/loan-program-row";
import { ShoppingRangeRow } from "./rows/shopping-range-row";
import type { ShoppingRangeState } from "../../../lib/shopping-range";
import { TargetGoalRow } from "./rows/target-goal-row";

const PROPERTY_TYPE_OPTIONS = [
  { value: "single_family", label: "Single family" },
  { value: "condo", label: "Condo" },
  { value: "townhome", label: "Townhome" },
  { value: "multi_family_2_4", label: "Multi-family (2–4 units)" },
];

const OCCUPANCY_OPTIONS = [
  { value: "primary", label: "Primary residence" },
  { value: "second_home", label: "Second home" },
  { value: "investment", label: "Investment" },
];

const TIMELINE_OPTIONS = [
  { value: "0_3_months", label: "ASAP (0–3 mo)" },
  { value: "3_6_months", label: "3–6 mo" },
  { value: "6_12_months", label: "6–12 mo" },
  { value: "12_plus", label: "Exploring" },
];

const BUYER_TYPE_OPTIONS = [
  { value: "first_time", label: "First-time buyer" },
  { value: "move_up", label: "Move-up buyer" },
  { value: "investor", label: "Investor" },
  { value: "downsizing", label: "Downsizing" },
];

const PROPERTY_TYPE_LABEL: Record<string, string> = {
  single_family: "Single family",
  condo: "Condo",
  townhome: "Townhome",
  multi_family_2_4: "Multi-family (2–4 units)",
};

const OCCUPANCY_LABEL: Record<string, string> = {
  primary: "Primary residence",
  second_home: "Second home",
  investment: "Investment",
};

const BUYER_TYPE_LABEL: Record<string, string> = {
  first_time: "First-time buyer",
  move_up: "Move-up buyer",
  investor: "Investor",
  downsizing: "Downsizing",
};

interface TargetCardProps {
  snapshot: DashboardSnapshot;
  target: ReadinessTarget | null | undefined;
  onAddProperty?: () => void;
  opts: UseTargetEditOptions;
  /**
   * When true, renders only the row list — no section header, no surface-card
   * wrapper. Used by TargetGoalsSection to embed rows in a flat divide-y list
   * consistent with the financial profile rows.
   */
  headless?: boolean;
  /**
   * When true (combined with headless), renders rows as a bare fragment with
   * no wrapping element. The caller is responsible for providing the
   * data-testid="target-card" wrapper. Used when rows are interleaved with
   * goal rows inside a shared divide-y container.
   */
  noWrapper?: boolean;
}

export function TargetCard({
  snapshot,
  target,
  onAddProperty,
  opts,
  headless = false,
  noWrapper = false,
}: TargetCardProps) {
  const [rangeRefreshVersion, setRangeRefreshVersion] = useState(0);
  const [shoppingRange, setShoppingRange] = useState<ShoppingRangeState | null>(
    null,
  );
  const propertyType = target?.deal.property.property_type ?? null;
  const occupancy = target?.deal.property.occupancy ?? null;
  const product = target?.deal.preferred_product ?? null;
  const termMo = target?.deal.preferred_term_mo ?? null;
  const buyerType = target?.deal.buyer_type ?? null;
  const timelineToken = target?.timeline ?? null;
  const isPropertyBackedTarget = isPropertyReadinessTarget(target);
  const targetPrice = targetPriceDollars(target, snapshot);
  const targetArea =
    isPropertyBackedTarget && target?.label
      ? target.label
      : displayReadinessTargetArea(target, snapshot.targetArea);
  const propertyLockedReason =
    "Set by the saved property. Edit the property to change it.";
  const propertyAction =
    isPropertyBackedTarget && target?.property_id
      ? {
          href: `/properties/${target.property_id}`,
          label: "View property",
        }
      : undefined;

  const rows = (
    <>
      <ShoppingRangeRow
        target={target}
        opts={opts}
        onRange={setShoppingRange}
        refreshVersion={rangeRefreshVersion}
      />
      <TargetGoalRow
        rowId="target_price"
        label="Target price"
        value={targetPrice > 0 ? formatCompactCurrency(targetPrice) : "Not set"}
        patchKey="price"
        inputType="currency"
        currentTargetValue={targetPrice > 0 ? targetPrice : null}
        warning="Changing target price recalculates buying power, payment range, and readiness guidance."
        readOnlyReason={
          isPropertyBackedTarget ? propertyLockedReason : undefined
        }
        midpointAvailable={Boolean(shoppingRange?.shopping_range)}
        midpointRevision={shoppingRange?.revision}
        opts={{ ...opts, onUncertain: () => { opts.onUncertain?.(); setRangeRefreshVersion((n) => n + 1); } }}
      />
      {target && targetPrice > 0 ? (
        <p className="px-4 py-2 text-sm text-muted-foreground">
          {isPropertyBackedTarget
            ? `Estimates use this home’s ${formatCompactCurrency(targetPrice)} price.`
            : target?.price_source === "range_midpoint"
              ? `Estimates use a ${formatCompactCurrency(targetPrice)} home, the midpoint of your shopping range.`
              : `Estimates use your ${formatCompactCurrency(targetPrice)} target price.`}
        </p>
      ) : null}

      <TargetGoalRow
        rowId="target_area"
        label="Target city or ZIP"
        value={targetArea || "Not set"}
        patchKey="location_code"
        inputType="target-location"
        currentTargetValue={targetArea || null}
        onAddProperty={onAddProperty}
        warning="Changing target area can change local tax, insurance, and market assumptions."
        readOnlyReason={
          isPropertyBackedTarget ? propertyLockedReason : undefined
        }
        readOnlyAction={propertyAction}
        opts={opts}
      />

      <TargetGoalRow
        rowId="property_type_occupancy"
        label="Property type"
        value={
          propertyType
            ? (PROPERTY_TYPE_LABEL[propertyType] ?? propertyType)
            : "Not set"
        }
        patchKey="property_type"
        inputType="select"
        options={PROPERTY_TYPE_OPTIONS}
        currentTargetValue={propertyType}
        opts={opts}
      />

      <TargetGoalRow
        rowId="occupancy"
        label="Occupancy"
        value={
          occupancy
            ? (OCCUPANCY_LABEL[occupancy] ?? occupancy)
            : "Primary residence"
        }
        patchKey="occupancy"
        inputType="select"
        options={OCCUPANCY_OPTIONS}
        currentTargetValue={occupancy ?? "primary"}
        opts={opts}
      />

      <LoanProgramRow product={product} termMo={termMo} opts={opts} />

      <TargetGoalRow
        rowId="timeline"
        label="Move-in timeline"
        value={timelineLabel(timelineToken) ?? snapshot.timeline ?? "Not set"}
        patchKey="timeline"
        inputType="select"
        options={TIMELINE_OPTIONS}
        currentTargetValue={timelineToken}
        opts={opts}
      />

      <TargetGoalRow
        rowId="buyer_type"
        label="Buyer type"
        value={
          buyerType ? (BUYER_TYPE_LABEL[buyerType] ?? buyerType) : "Not set"
        }
        patchKey="buyer_type"
        inputType="select"
        options={BUYER_TYPE_OPTIONS}
        currentTargetValue={buyerType}
        opts={opts}
      />
    </>
  );

  if (headless) {
    if (noWrapper) {
      // Bare fragment — caller owns the data-testid="target-card" wrapper.
      return <>{rows}</>;
    }
    // Rows only — no header, no surface-card shell. The data-testid is still
    // on a wrapper div so tests querying '[data-testid="target-card"]' still find it.
    return <div data-testid="target-card">{rows}</div>;
  }

  return (
    <section
      aria-labelledby="target-card-heading"
      className="space-y-3"
      data-testid="target-card"
    >
      <header className="space-y-1.5 px-1">
        <p className="eyebrow-label">Target</p>
        <h2
          id="target-card-heading"
          className="text-base font-semibold tracking-[-0.02em] text-foreground"
        >
          What you're shopping for
        </h2>
        <p className="max-w-xl text-sm leading-6 text-muted-foreground">
          Editing here changes the deal Sill prices and qualifies.
        </p>
      </header>

      <div className="surface-card overflow-hidden bg-card">
        <div className="divide-y divide-border-soft">{rows}</div>
      </div>
    </section>
  );
}

function targetPriceDollars(
  target: ReadinessTarget | null | undefined,
  snapshot: DashboardSnapshot,
): number {
  if (isPropertyReadinessTarget(target) && target?.property_id) {
    const trackedProperty = snapshot.trackedProperties?.find(
      (property) => property.id === target.property_id,
    );
    if (trackedProperty?.listPrice && trackedProperty.listPrice > 0) {
      return trackedProperty.listPrice;
    }
  }
  const rawPrice = target?.deal.property.target_price;
  if (typeof rawPrice === "number" && rawPrice > 0) {
    return Math.round(rawPrice / 100);
  }
  return snapshot.targetPrice;
}

function timelineLabel(token: string | null | undefined): string | null {
  switch (token) {
    case "0_3_months":
      return "ASAP (0–3 mo)";
    case "3_6_months":
      return "3–6 mo";
    case "6_12_months":
      return "6–12 mo";
    case "12_plus":
      return "Exploring";
    default:
      return null;
  }
}
